/**
 * Lightweight inline markdown for cloud term-sheet copy (admin preview).
 * Supports: **bold**, __bold__, *italic* (nesting allowed).
 * Single underscores are left literal so snake_case / mid-word _ is safe.
 * Unmatched delimiters are kept as literal text.
 */

export type InlineMdNode =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: InlineMdNode[] }
  | { type: 'italic'; children: InlineMdNode[] };

type Delimiter = '**' | '__' | '*';

const OPENERS: Delimiter[] = ['**', '__', '*'];

function styleFor(delim: Delimiter): 'bold' | 'italic' {
  return delim === '*' ? 'italic' : 'bold';
}

function startsWithDelim(source: string, i: number, delim: Delimiter): boolean {
  return source.startsWith(delim, i);
}

function parseUntil(
  source: string,
  start: number,
  stop: Delimiter | null
): { nodes: InlineMdNode[]; end: number; closed: boolean } {
  const nodes: InlineMdNode[] = [];
  let i = start;
  let text = '';

  const flush = () => {
    if (text) {
      nodes.push({ type: 'text', text });
      text = '';
    }
  };

  while (i < source.length) {
    if (stop && startsWithDelim(source, i, stop)) {
      flush();
      return { nodes, end: i + stop.length, closed: true };
    }

    let opened: Delimiter | null = null;
    for (const delim of OPENERS) {
      if (!startsWithDelim(source, i, delim)) continue;
      if (delim === '*' && startsWithDelim(source, i, '**')) {
        continue;
      }
      opened = delim;
      break;
    }

    if (opened) {
      const inner = parseUntil(source, i + opened.length, opened);
      if (inner.closed) {
        flush();
        nodes.push({ type: styleFor(opened), children: inner.nodes });
        i = inner.end;
        continue;
      }
      text += opened;
      i += opened.length;
      continue;
    }

    text += source[i];
    i += 1;
  }

  flush();
  return { nodes, end: i, closed: false };
}

export function parseInlineMarkdown(source: string): InlineMdNode[] {
  if (!source) return [];
  return parseUntil(source, 0, null).nodes;
}

export function stripInlineMarkdown(source: string): string {
  const walk = (nodes: InlineMdNode[]): string =>
    nodes.map((n) => (n.type === 'text' ? n.text : walk(n.children))).join('');
  return walk(parseInlineMarkdown(source));
}

/** Short hint shown under editable term-sheet text fields. */
export const INLINE_MARKDOWN_HINT = 'Supports **bold** and *italic* markdown.';
